import { ListSubheader, TextField, useMediaQuery, useTheme } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import React, { Children, cloneElement, ComponentType, createContext, forwardRef, HTMLAttributes, isValidElement, memo, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { FixMeLater } from 'types/FixMeLaterType';

const filter = createFilterOptions();

const useStyles = makeStyles({
    listbox: {
        boxSizing: 'border-box',
        '& ul': {
          padding: 0,
          margin: 0,
        },
      },
})

interface TextboxProps {
    label: string,
    value?: string[],
    handleChange: (data: string[]) => void,
    disabled?: boolean,
    options?: Option[]
}

export interface Option {
    title: string,
    inputValue: string
}

/*
    Create context to be passed down to children of ListboxComponent
*/
const OuterElementContext = createContext({});

/*
    Create wrapper for passing context and refereference to nested components
*/
const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

/*
    Adapter for react-window to allow virtualisation of cells in Textbox Dropdown List
*/
const ListboxComponent = forwardRef<HTMLDivElement>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = Children.toArray(children);
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const LISTBOX_PADDING = 8; // px

    function renderRow(props: ListChildComponentProps) {
        const { data, index, style } = props;
        return cloneElement(data[index], {
            style: {
                ...style,
                top: (style.top as number) + LISTBOX_PADDING,
            },
        });
    }

    function useResetCache(data: any) {
        const ref = useRef<VariableSizeList>(null);
        useEffect(() => {
            if (ref.current != null) {
                ref.current.resetAfterIndex(0, true);
            }
        }, [data]);
        return ref;
    }

    const getChildSize = (child: ReactNode) => {
        if (isValidElement(child) && child.type === ListSubheader) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            console.log(8 * (itemSize ))
            return 8 * (itemSize );
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div
         ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

function Textbox(props: TextboxProps) {

    const textbox = useRef<HTMLDivElement>(null)

    const [customOptions, setCustomOptions] = useState<Option[]>([])

    function handleChange(data: string[]) {
        props.handleChange(data)
    }

    const classes = useStyles()

    const generateOptionsMap = useCallback(
        (): { [index: string]: Option } => {
            const optionsMap: { [index: string]: Option } = {}

            if (props.options != null) {
                const allOptions = customOptions.concat(props.options)

                allOptions.forEach((option: Option) => {
                    optionsMap[option.inputValue] = option
                });
            }

            return optionsMap
        }, [props.options, customOptions, setCustomOptions]
    )

    const generateSelectedOptionsMap = useCallback(
        (): { [index: string]: Option } => {
            const optionsMap = generateOptionsMap()
            const selectedOptionsMap: { [index: string]: Option } = {}

            props.value?.forEach((optionKey: string) => {
                const optionValue = optionsMap[optionKey]
                if (optionValue != null) {
                    selectedOptionsMap[optionKey] = optionValue
                } else {
                    selectedOptionsMap[optionKey] = { title: optionKey, inputValue: optionKey }
                }
            });

            return selectedOptionsMap
        }, [props.value, generateOptionsMap]
    )

    return (
        <Autocomplete
            id={`autocomplete-${props.label}`}
            classes={classes}
            options={Object.values(generateOptionsMap())}
            value={Object.values(generateSelectedOptionsMap())}
            multiple
            fullWidth
            openOnFocus
            limitTags={2}
            renderOption={(option: Option) => option.title}
            closeIcon={<CancelIcon />}
            clearOnEscape
            autoHighlight
            disabled={props.disabled}
            freeSolo
            getOptionLabel={(option: Option | string) => {
                // e.g value selected with enter, right from the input
                if (typeof option === 'string') {
                    return option;
                }
                if (option.title) {
                    return option.title;
                }
                return option.inputValue;
            }}
            ChipProps={{ innerRef: textbox, size: "small", variant: "outlined" }}
            onChange={(_, newValue: Option[]) => { // [{title:"a" input:"a"}] or ["a"]
                if (Array.isArray(newValue)) {
                    const optionsMap = generateOptionsMap()

                    const newData = newValue.map((item: Option) => item.inputValue)
                        .filter((value, index, self) => self.indexOf(value) === index)

                    const newCustomOptions: Option[] = newData
                        .filter(dataValue => optionsMap[dataValue] == null)
                        .map(item => ({ title: item, inputValue: item }))
                    setCustomOptions(customOptions.concat(newCustomOptions))

                    handleChange(newData);
                }
            }}
            filterOptions={(options: Option[] | string[], params: FixMeLater) => {
                const optionsValueMap = generateSelectedOptionsMap()
                const filtered = filter(options, params)
                    .filter((option: Option) => optionsValueMap[option.inputValue] == null);

                if (params.inputValue !== '') {
                    filtered.push({
                        inputValue: params.inputValue,
                        title: `Add "${params.inputValue}"`,
                    });
                }

                return filtered;
            }}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        label={props.label}
                        variant="outlined"
                        margin="dense"
                        rows={1}
                        rowsMax={1}
                    />
                )
            }}
            ListboxComponent={ListboxComponent as ComponentType<HTMLAttributes<HTMLElement>>}
        />
    )
}

Textbox.propTypes = {
    label: PropTypes.string,
    value: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        inputValue: PropTypes.string
    })),
    handleChange: PropTypes.func,
    disabled: PropTypes.bool
}

export default memo(Textbox)

